import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  isAuthenticated,
  getCurrentUserId,
  logout,
  isEmailNotVerifiedError,
  updateUser,
} from "../utils/auth";
import {
  fetchOnboardingState,
  isOnboardingActive,
} from "../api/onboarding";

const isSetupPath = (pathname) =>
  pathname === "/setup" ||
  pathname.startsWith("/setup/") ||
  pathname.startsWith("/setup?");

const getOnboardingCacheKey = (userId) => `onboarding_status:${userId}`;

const clearCompletedOnboardingCache = (userId) => {
  sessionStorage.removeItem(getOnboardingCacheKey(userId));
};

const writeCompletedOnboardingCache = (userId, onboardingState) => {
  if (
    onboardingState?.status !== "completed" &&
    onboardingState?.status !== "skipped"
  ) {
    return;
  }

  sessionStorage.setItem(
    getOnboardingCacheKey(userId),
    JSON.stringify({
      status: onboardingState.status,
      currentStep: onboardingState.currentStep,
    }),
  );
};

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <p className="text-gray-600">Loading...</p>
  </div>
);

const ErrorScreen = ({ message, onRetry }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
    <div className="max-w-md w-full bg-white rounded-lg shadow p-6 text-center">
      <h1 className="text-xl font-semibold text-gray-900 mb-2">
        Could not verify your account
      </h1>
      <p className="text-gray-600 mb-4">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
      >
        Retry
      </button>
    </div>
  </div>
);

export const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [phase, setPhase] = useState("checking");
  const [errorMessage, setErrorMessage] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    const run = async () => {
      if (!isAuthenticated()) {
        sessionStorage.setItem("intended_route", location.pathname);
        navigate("/login");
        return;
      }

      const userId = getCurrentUserId();
      if (!userId) {
        sessionStorage.setItem("intended_route", location.pathname);
        navigate("/login");
        return;
      }

      // Always ask the server (requireVerifiedUser on onboarding API). Do not
      // gate on localStorage verified — it is often missing for Google logins
      // and after clicking the email verification link.
      const result = await fetchOnboardingState(userId, controller.signal);
      if (controller.signal.aborted) return;

      if (!result.ok) {
        if (isEmailNotVerifiedError(result.error)) {
          clearCompletedOnboardingCache(userId);
          updateUser({ verified: false });
          navigate("/verify-email", { replace: true });
          return;
        }
        if (result.error.status === 401 || result.error.status === 403) {
          // Token rejected: clear local auth state before sending the user to
          // /login. Without this, LoginPage's isAuthenticated() check is true
          // (it only inspects localStorage userId/tokenExpiry, not validity)
          // and bounces the user back to /dashboard, creating a redirect loop.
          logout();
          sessionStorage.setItem("intended_route", location.pathname);
          navigate("/login");
          return;
        }
        setErrorMessage(result.error.message);
        setPhase("error");
        return;
      }

      writeCompletedOnboardingCache(userId, result.value);
      updateUser({ verified: true });

      if (
        isOnboardingActive(result.value) &&
        !isSetupPath(location.pathname)
      ) {
        navigate("/setup", { replace: true });
        return;
      }

      setPhase("ready");
    };

    // Reset to loading state on every retry (retryNonce change). The
    // synchronous setState before the async run() is intentional, the
    // resulting re-render is the loading-screen flash we want users to see
    // while the new check is in flight.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPhase("checking");
    setErrorMessage(null);
    run();

    return () => controller.abort();
  }, [navigate, location.pathname, retryNonce]);

  if (phase === "checking") {
    return <LoadingScreen />;
  }

  if (phase === "error") {
    return (
      <ErrorScreen
        message={errorMessage ?? "Unknown error"}
        onRetry={() => setRetryNonce((n) => n + 1)}
      />
    );
  }

  return children;
};
